import { NotificationNoReadEmptyState } from 'ui/illustrations/NotificationNoReadEmptyState';
import { ErrorStatePageError } from 'ui/illustrations';
import { body2 } from 'ui/theme';
import styled from 'styled-components';
import { FC } from 'react';
import { Skeleton } from 'ui/skeleton';
import {
  JourneyReviewStateContainer,
  JourneyReviewStateTitle,
  JourneyReviewStateDescription,
  ColumnTextBlock,
  SkeletonRow,
  ContentDivider,
  SkeletonContainer,
} from './JourneyReviewState.styles';
import { StyledBody } from '../JourneyReview.styles';

type JourneyReviewStateProps = {
  title: string;
  description: string | JSX.Element;
  Illustration: React.FC;
};

export const JOURNEY_REVIEW_SKELETON = 'journey-review-skeleton';
export const JOURNEY_REVIEW_ISSUES_NOT_FOUND = 'journey-review-issues-not-found';
export const JOURNEY_REVIEW_PERFORMANCE_NOT_FOUND = 'journey-review-performance-not-found';
export const JOURNEY_REVIEW_ISSUES_ERROR = 'journey-review-issues-error';
export const JOURNEY_REVIEW_PERFORMANCE_ERROR = 'journey-review-performance-error';
export const JOURNEY_RETRY_LINK = 'journey-retry-link';

const BaseJourneyReviewState = ({
  Illustration,
  title,
  description,
  ...props
}: JourneyReviewStateProps) => {
  return (
    <StyledBody>
      <JourneyReviewStateContainer {...props}>
        <Illustration />
        <JourneyReviewStateTitle>{title}</JourneyReviewStateTitle>
        <JourneyReviewStateDescription>{description}</JourneyReviewStateDescription>
      </JourneyReviewStateContainer>
    </StyledBody>
  );
};

export const ReviewPanelSkeleton: FC = () => {
  return (
    <SkeletonContainer data-test={JOURNEY_REVIEW_SKELETON}>
      <SkeletonRow>
        <Skeleton height={50} width={60} />
        <ColumnTextBlock>
          <Skeleton height={20} width={200} />
          <Skeleton height={16} width={150} />
        </ColumnTextBlock>
      </SkeletonRow>
      <ContentDivider />
      <SkeletonRow>
        <Skeleton height={50} width={60} />
        <ColumnTextBlock>
          <Skeleton height={20} width={200} />
          <Skeleton height={16} width={150} />
        </ColumnTextBlock>
      </SkeletonRow>
      <ContentDivider />
      <SkeletonRow>
        <Skeleton height={50} width={60} />
        <ColumnTextBlock>
          <Skeleton height={20} width={200} />
          <Skeleton height={16} width={150} />
        </ColumnTextBlock>
      </SkeletonRow>
    </SkeletonContainer>
  );
};

export const LoadingItems = () => {
  return <ReviewPanelSkeleton />;
};

const ErrorLoadingItems = ({ description, ...props }: { description: string | JSX.Element }) => {
  return (
    <BaseJourneyReviewState
      {...props}
      title="We've hit a snag"
      description={description}
      Illustration={ErrorStatePageError}
    />
  );
};

const StyledLink = styled.span`
  ${body2};
  color: var(--text-link, #79347f);
`;

type ErrorLoadingProps = {
  retry: () => void;
};

const Retry = ({ retry }: ErrorLoadingProps) => {
  return (
    <StyledLink data-test={JOURNEY_RETRY_LINK} onClick={retry}>
      try refreshing
    </StyledLink>
  );
};

export const ErrorLoadingPerformanceIssues = ({ retry }: ErrorLoadingProps) => {
  const description = (
    <>
      Sorry, we&apos;re having trouble loading performance suggestions for this journey. Come back
      later or <Retry retry={retry} />.
    </>
  );
  return (
    <ErrorLoadingItems data-test={JOURNEY_REVIEW_PERFORMANCE_ERROR} description={description} />
  );
};

export const ErrorLoadingIssues = ({ retry }: ErrorLoadingProps) => {
  const description = (
    <>
      Sorry, we&apos;re having trouble loading issues for this journey. Come back later or{' '}
      <Retry retry={retry} />.
    </>
  );
  return <ErrorLoadingItems data-test={JOURNEY_REVIEW_ISSUES_ERROR} description={description} />;
};

const NoItemsFound = ({ description, ...props }: { description: string | JSX.Element }) => {
  return (
    <BaseJourneyReviewState
      {...props}
      title="Everything looks great!"
      description={description}
      Illustration={NotificationNoReadEmptyState}
    />
  );
};

export const NoPerformanceIssuesFound = () => {
  return (
    <NoItemsFound
      data-test={JOURNEY_REVIEW_PERFORMANCE_NOT_FOUND}
      description="There are currently no suggestions for improving your journey's performance."
    />
  );
};
export const NoIssuesFound = () => {
  return (
    <NoItemsFound
      data-test={JOURNEY_REVIEW_ISSUES_NOT_FOUND}
      description="This journey currently has no setup errors."
    />
  );
};
